import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { NetworkService } from './network.service';
import { MutationQueue } from './mutation-queue.service';
import { NotificationService } from 'src/app/services/notification.service';

/**
 * Badge global affiché dans la topbar pour signaler l'état réseau et le
 * nombre d'actions en attente de synchronisation.
 *
 * <p>Trois états visuels :</p>
 * <ul>
 *   <li><b>Online + queue vide</b> → discret (success ghost), affiché uniquement
 *       quand on survole — pas de bruit visuel quand tout va bien.</li>
 *   <li><b>Online + queue non vide</b> → badge orange « N en attente », clic
 *       redirige vers la page Sync.</li>
 *   <li><b>Offline</b> → badge rouge « Hors ligne », clic redirige vers Sync.</li>
 * </ul>
 *
 * <p>Pensé pour Niamey : un atelier qui passe 2h en zone sans 4G doit
 * comprendre en un coup d'œil que ses actions continuent à être enregistrées
 * localement, et que la sync se fera dès que possible.</p>
 */
@Component({
  selector: 'app-network-badge',
  standalone: true,
  imports: [CommonModule, RouterModule, MaterialModule, TablerIconsModule],
  template: `
    <a routerLink="/dinki/sync"
       class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors"
       [ngClass]="badgeClass()"
       [matTooltip]="tooltip()">
      <i-tabler [name]="iconName()" class="size-3.5"></i-tabler>
      <span class="hidden sm:inline">{{ label() }}</span>
      @if (pendingCount > 0) {
        <span class="ms-1 px-1.5 py-0.5 rounded-full bg-white/30 text-[10px] font-bold">
          {{ pendingCount }}
        </span>
      }
    </a>
  `,
})
export class NetworkBadgeComponent implements OnInit, OnDestroy {
  private network = inject(NetworkService);
  private queue = inject(MutationQueue);
  private notif = inject(NotificationService);

  online = true;
  pendingCount = 0;
  private wasOffline = false;

  private subs: Subscription[] = [];

  ngOnInit(): void {
    this.subs.push(
      this.network.online$.subscribe((on) => {
        // Transition offline → online : on signale poliment que la sync a démarré.
        if (on && this.wasOffline) {
          this.notif.success(
            'Connexion rétablie — synchronisation en cours.',
            'En ligne',
          );
        }
        this.wasOffline = !on;
        this.online = on;
      }),
      this.queue.pending$.subscribe((n) => (this.pendingCount = n)),
      // Toast récapitulatif quand la file est entièrement vidée.
      this.queue.drained$.subscribe((replayed) => {
        this.notif.success(
          replayed > 1
            ? `${replayed} actions synchronisées avec succès.`
            : 'Action synchronisée avec succès.',
          'Synchronisation terminée',
        );
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  iconName(): string {
    if (!this.online) return 'wifi-off';
    if (this.pendingCount > 0) return 'cloud-upload';
    return 'wifi';
  }

  label(): string {
    if (!this.online) return 'Hors ligne';
    if (this.pendingCount > 0) return 'En attente';
    return 'En ligne';
  }

  tooltip(): string {
    if (!this.online) {
      return `Vous travaillez hors ligne. ${this.pendingCount} action(s) en attente. Cliquer pour ouvrir la page Synchronisation.`;
    }
    if (this.pendingCount > 0) {
      return `${this.pendingCount} action(s) en cours de synchronisation avec le serveur.`;
    }
    return 'Connecté au serveur Dinki.';
  }

  badgeClass(): string {
    if (!this.online) {
      return 'bg-error text-white hover:bg-error/90';
    }
    if (this.pendingCount > 0) {
      return 'bg-warning text-white hover:bg-warning/90';
    }
    return 'bg-success/10 text-success hover:bg-success/20';
  }
}
